import React from "react";

const ArrowIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4 text-fut-primary shrink-0 mt-1" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PackagesPricingPage = ({ lang = "es" }) => {
  const isSpanish = lang === "es";

  const t = isSpanish
    ? {
        eyebrow: "Paquetes / Soluciones",
        title: "Rutas diseñadas para ordenar tu negocio según su etapa.",
        subtitle:
          "No todos los negocios necesitan lo mismo. Elige la ruta que más se parece a tu situación y solicita una evaluación inicial para confirmar el siguiente paso.",
        primaryCta: "Solicitar evaluación",
        secondaryCta: "Ver paquetes",
        authorityTitle: "Primero claridad. Después acción.",
        authorityText:
          "Antes de recomendar una ruta, revisamos tu situación, documentos, estructura y objetivo principal. La meta no es venderte más cosas; es ayudarte a identificar qué debes ordenar primero.",
        authorityPoints: [
          "Evaluación inicial",
          "Ruta recomendada",
          "Ruta por etapa",
          "Sin promesas falsas",
        ],
        transparencyTitle: "Trabajamos con transparencia",
        transparencyText:
          "No prometemos aprobaciones, no recomendamos rutas sin revisar el caso y no usamos lenguaje confuso para presionar una decisión.",
        packagesTitle: "Selecciona la ruta que más se parece a tu situación",
        packages: [
          {
            tag: "Para iniciar",
            title: "Base Empresarial",
            line:
              "Empieza con una base empresarial ordenada antes de mezclar impuestos, cuentas o decisiones financieras.",
            fit:
              "Para emprendedores que están iniciando o necesitan formalizar correctamente su negocio.",
            includes: [
              "LLC / Corporation",
              "EIN",
              "Documentación base",
              "Revisión de estructura inicial",
              "Orientación del siguiente paso",
            ],
            cta: "Solicitar evaluación de estructura",
          },
          {
            tag: "Persona / Self-employed",
            title: "Orden Fiscal Personal",
            line: "Declara con claridad, no con improvisación.",
            fit:
              "Para personas, familias, trabajadores W-2, 1099, self-employed o contribuyentes con documentos pendientes.",
            includes: [
              "Declaración personal",
              "Anexos cuando apliquen",
              "ITIN / W-7 si aplica",
              "Cartas del IRS",
              "Enmiendas o planes de pago si aplica",
            ],
            cta: "Solicitar revisión fiscal personal",
          },
          {
            tag: "Empresa / LLC / Corp",
            title: "Orden Fiscal Empresarial",
            line:
              "Mantén tu empresa fiscalmente organizada antes de que el desorden se vuelva costoso.",
            fit:
              "Para LLC, S-Corp, C-Corp, negocios con socios o empresas que necesitan orden fiscal.",
            includes: [
              "Declaraciones corporativas",
              "Extensiones",
              "Pagos estimados",
              "Reportes relacionados cuando apliquen",
              "Revisión de clasificación fiscal",
            ],
            cta: "Solicitar revisión fiscal empresarial",
          },
          {
            tag: "Presencia verificable",
            title: "Perfil Empresarial",
            line:
              "Antes de presentarte ante bancos, proveedores o sistemas comerciales, tu empresa debe verse ordenada.",
            fit:
              "Para negocios existentes que necesitan verse más organizados, verificables y profesionales.",
            includes: [
              "DUNS",
              "Correo corporativo",
              "Teléfono comercial",
              "Website básico",
              "Cuentas proveedoras iniciales",
              "Plan de negocios base",
            ],
            cta: "Solicitar revisión de perfil empresarial",
          },
          {
            tag: "Preparación financiera",
            title: "Mapa de Capital",
            line:
              "No toda ruta de capital conviene. Primero hay que saber dónde estás parado.",
            fit:
              "Para dueños que quieren entender qué rutas podrían tener sentido según su situación actual.",
            includes: [
              "Diagnóstico empresarial",
              "Revisión documental",
              "Dependencia del dueño",
              "Preparación financiera",
              "2 a 3 rutas posibles",
              "Siguiente paso recomendado",
            ],
            note:
              "No prometemos aprobación. Evaluamos preparación, documentos y posibles rutas.",
            cta: "Solicitar mapa de capital",
          },
          {
            tag: "Ruta completa",
            title: "Acompañamiento Estratégico",
            line:
              "Una ruta más completa para dueños que necesitan claridad, orden y acompañamiento antes de crecer.",
            fit:
              "Para negocios que necesitan ordenar estructura, impuestos, documentos y preparación financiera al mismo tiempo.",
            includes: [
              "Revisión de estructura",
              "Revisión fiscal",
              "Perfil empresarial",
              "Documentación base",
              "Mapa de capital",
              "Plan de acción",
              "Sesiones de orientación",
            ],
            cta: "Solicitar evaluación estratégica",
          },
        ],
        finalTitle: "¿No sabes cuál ruta elegir?",
        finalText:
          "Solicita una evaluación inicial. Revisamos tu situación y te indicamos qué ruta tiene más sentido antes de avanzar.",
        finalCta: "Solicitar evaluación inicial",
        callLabel: "Llamar",
        whatsappLabel: "WhatsApp",
      }
    : {
        eyebrow: "Packages / Solutions",
        title: "Routes designed to organize your business based on its stage.",
        subtitle:
          "Not every business needs the same thing. Choose the route that looks most like your situation and request an initial evaluation to confirm the next step.",
        primaryCta: "Request evaluation",
        secondaryCta: "View packages",
        authorityTitle: "Clarity first. Action after.",
        authorityText:
          "Before recommending a route, we review your situation, documents, structure, and main objective. The goal is not to sell more things; it is to identify what should be organized first.",
        authorityPoints: [
          "Initial evaluation",
          "Recommended route",
          "Stage-based route",
          "No false promises",
        ],
        transparencyTitle: "We work with transparency",
        transparencyText:
          "We do not promise approvals, we do not recommend routes without reviewing the case, and we do not use confusing language to pressure a decision.",
        packagesTitle: "Select the route that looks most like your situation",
        packages: [
          {
            tag: "Starting point",
            title: "Business Foundation",
            line:
              "Start with an organized business foundation before mixing taxes, accounts, or financial decisions.",
            fit:
              "For entrepreneurs starting out or needing to properly formalize their business.",
            includes: [
              "LLC / Corporation",
              "EIN",
              "Base documentation",
              "Initial structure review",
              "Next-step guidance",
            ],
            cta: "Request structure evaluation",
          },
          {
            tag: "Individual / Self-employed",
            title: "Personal Tax Order",
            line: "File with clarity, not improvisation.",
            fit:
              "For individuals, families, W-2 workers, 1099 workers, self-employed taxpayers, or pending documents.",
            includes: [
              "Personal return",
              "Schedules when applicable",
              "ITIN / W-7 if applicable",
              "IRS letters",
              "Amendments or payment plans if applicable",
            ],
            cta: "Request personal tax review",
          },
          {
            tag: "Business / LLC / Corp",
            title: "Business Tax Order",
            line:
              "Keep your company fiscally organized before disorder becomes costly.",
            fit:
              "For LLCs, S-Corps, C-Corps, businesses with partners, or companies that need tax order.",
            includes: [
              "Corporate returns",
              "Extensions",
              "Estimated payments",
              "Related reports when applicable",
              "Tax classification review",
            ],
            cta: "Request business tax review",
          },
          {
            tag: "Verifiable presence",
            title: "Business Profile",
            line:
              "Before presenting your company to banks, vendors, or business systems, it should look organized.",
            fit:
              "For existing businesses that need to look more organized, verifiable, and professional.",
            includes: [
              "DUNS",
              "Corporate email",
              "Business phone",
              "Basic website",
              "Initial vendor accounts",
              "Base business plan",
            ],
            cta: "Request business profile review",
          },
          {
            tag: "Financial preparation",
            title: "Capital Map",
            line:
              "Not every capital route makes sense. First, you need to know where you stand.",
            fit:
              "For owners who want to understand which routes may make sense based on their current situation.",
            includes: [
              "Business diagnosis",
              "Document review",
              "Owner dependency",
              "Financial readiness",
              "2 to 3 possible routes",
              "Recommended next step",
            ],
            note:
              "We do not promise approval. We evaluate readiness, documents, and possible routes.",
            cta: "Request capital map",
          },
          {
            tag: "Complete route",
            title: "Strategic Guidance",
            line:
              "A more complete route for owners who need clarity, order, and guidance before growing.",
            fit:
              "For businesses that need to organize structure, taxes, documents, and financial readiness at the same time.",
            includes: [
              "Structure review",
              "Tax review",
              "Business profile",
              "Base documentation",
              "Capital map",
              "Action plan",
              "Guidance sessions",
            ],
            cta: "Request strategic evaluation",
          },
        ],
        finalTitle: "Not sure which route to choose?",
        finalText:
          "Request an initial evaluation. We review your situation and show you which route makes the most sense before moving forward.",
        finalCta: "Request initial evaluation",
        callLabel: "Call",
        whatsappLabel: "WhatsApp",
      };


  const premiumPlans = isSpanish
    ? [
        {
          tag: "Acompañamiento anual",
          title: "Plan Starter",
          line:
            "Para emprendedores, freelancers, LLC de un solo miembro o negocios en etapa inicial que necesitan estructura y formalidad.",
          objective:
            "Crear una base empresarial, fiscal y comercial para que el negocio opere con más orden y presencia profesional.",
          includes: [
            "Revisión de estructura empresarial",
            "Correcciones básicas si aplican",
            "Perfil comercial inicial",
            "Correo corporativo y teléfono comercial",
            "DUNS y vendors iniciales",
            "Introducción a reportes financieros",
            "Declaración personal con Schedule C cuando aplique",
            "Extensiones y pagos estimados si aplican",
            "Educación financiera mensual",
            "Soporte durante el año",
          ],
          result:
            "El negocio queda más estructurado, formal y con una base inicial para operar con mayor claridad.",
          cta: "Solicitar evaluación Starter",
        },
        {
          tag: "Continuidad empresarial",
          title: "Plan Growth",
          line:
            "Para negocios que ya generan ingresos y necesitan ordenar contabilidad, fiscalidad y reportes financieros.",
          objective:
            "Organizar la información financiera y fiscal para que el dueño entienda mejor sus números y tome mejores decisiones.",
          includes: [
            "Todo lo del Plan Starter",
            "Bookkeeping mensual en QuickBooks",
            "Categorización de transacciones",
            "Conciliaciones bancarias y de tarjeta",
            "Cierre mensual",
            "Reportes financieros básicos",
            "P&L, balance y cash flow básico",
            "Tax planning básico",
            "Optimización de deducciones",
            "Seguimiento del perfil comercial",
          ],
          result:
            "El negocio gana más claridad financiera, reportes más útiles y mejor organización para sostener crecimiento.",
          cta: "Solicitar evaluación Growth",
        },
      ]
    : [
        {
          tag: "Annual guidance",
          title: "Starter Plan",
          line:
            "For entrepreneurs, freelancers, single-member LLCs, or early-stage businesses that need structure and formality.",
          objective:
            "Create a business, tax, and commercial foundation so the business can operate with more order and professional presence.",
          includes: [
            "Business structure review",
            "Basic corrections when applicable",
            "Initial commercial profile",
            "Corporate email and business phone",
            "DUNS and initial vendors",
            "Introduction to financial reports",
            "Personal return with Schedule C when applicable",
            "Extensions and estimated payments when applicable",
            "Monthly financial education",
            "Support during the year",
          ],
          result:
            "The business becomes more structured, formal, and supported by an initial foundation for clearer operations.",
          cta: "Request Starter evaluation",
        },
        {
          tag: "Business continuity",
          title: "Growth Plan",
          line:
            "For businesses already generating revenue that need bookkeeping, tax organization, and clearer financial reports.",
          objective:
            "Organize financial and tax information so the owner understands the numbers better and makes better decisions.",
          includes: [
            "Everything in the Starter Plan",
            "Monthly bookkeeping in QuickBooks",
            "Transaction categorization",
            "Bank and card reconciliations",
            "Monthly closing",
            "Basic financial reports",
            "P&L, balance sheet, and basic cash flow",
            "Basic tax planning",
            "Deduction optimization",
            "Commercial profile follow-up",
          ],
          result:
            "The business gains more financial clarity, more useful reports, and better organization to support growth.",
          cta: "Request Growth evaluation",
        },
      ];

  const featured = t.packages[5];
  const standard = t.packages.slice(0, 5);

  return (
    <main className="min-h-screen bg-[#050B14] text-fut-text-light font-sans pb-20 md:pb-0 overflow-hidden">
      <section className="relative px-6 py-12 md:py-16 pt-32 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(0,212,255,.26),transparent_27%),radial-gradient(circle_at_84%_28%,rgba(59,130,246,.20),transparent_30%),linear-gradient(135deg,#001A33_0%,#06111f_42%,#020617_100%)]" />
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-fut-primary/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-28 w-[520px] h-[520px] rounded-full bg-blue-500/10 blur-3xl" />

        <div className="relative container mx-auto max-w-screen-xl">
          <div className="grid lg:grid-cols-[.95fr_1.05fr] gap-8 items-stretch mb-8">
            <div className="flex flex-col justify-center py-8">
              <p className="text-fut-primary font-bold uppercase tracking-[.22em] mb-4">
                {t.eyebrow}
              </p>

              <h1 className="text-4xl md:text-6xl font-display font-black leading-tight mb-5 text-white">
                {t.title}
              </h1>

              <p className="text-lg md:text-xl text-fut-text-light/90 leading-relaxed max-w-3xl mb-8">
                {t.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#packages"
                  className="inline-flex items-center justify-center gap-2 bg-fut-primary text-fut-bg-dark px-7 py-4 rounded-2xl font-black shadow-xl shadow-fut-primary/12"
                >
                  {t.secondaryCta}
                  <ArrowIcon />
                </a>

                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 border border-fut-primary text-fut-primary px-7 py-4 rounded-2xl font-bold bg-white/5"
                >
                  {t.primaryCta}
                </a>
              </div>
            </div>

            <div className="relative rounded-[3rem] bg-white/[.045] shadow-2xl shadow-fut-primary/10 overflow-hidden min-h-[430px]">
              <img
                src="/images/packages-hero.png"
                alt="San Jose Obrero LLC paquetes empresariales"
                className="absolute inset-0 w-full h-full object-cover object-[center_18%] opacity-65"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#001A33]/82 via-[#06111f]/50 to-black/86" />

              <div className="relative z-10 h-full p-7 md:p-9 flex flex-col justify-end">
                <div className="rounded-[2.35rem] bg-black/28 backdrop-blur-xl p-6">
                  <p className="text-fut-primary text-sm font-black uppercase tracking-[.18em] mb-3">
                    {t.authorityTitle}
                  </p>

                  <p className="text-fut-text-light/90 text-lg leading-relaxed mb-5">
                    {t.authorityText}
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {t.authorityPoints.map((point) => (
                      <div
                        key={point}
                        className="rounded-2xl bg-white/[.05] p-3 flex gap-2"
                      >
                        <CheckIcon />
                        <span className="text-sm text-fut-text-light/90 font-semibold">
                          {point}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            id="packages"
            className="relative rounded-[3rem] bg-white/[.055] shadow-2xl shadow-fut-primary/10 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#001A33]/96 via-[#06111f]/93 to-black/96" />
            <div className="absolute left-0 top-0 h-full w-1 bg-fut-primary" />

            <div className="relative z-10 p-5 md:p-8 lg:p-10">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
                <div>
                  <p className="text-fut-primary font-black uppercase tracking-[.18em] text-sm mb-3">
                    San Jose Obrero LLC
                  </p>

                  <h2 className="text-3xl md:text-5xl font-display font-black leading-tight text-white">
                    {t.packagesTitle}
                  </h2>
                </div>

                <div className="rounded-2xl bg-black/22 p-5 max-w-xl">
                  <p className="text-fut-primary font-black mb-2">
                    {t.transparencyTitle}
                  </p>

                  <p className="text-fut-text-light/75 text-sm leading-relaxed">
                    {t.transparencyText}
                  </p>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {standard.map((pkg) => (
                  <article
                    key={pkg.title}
                    className="rounded-[2.35rem] bg-white/[.05] backdrop-blur-xl p-6 flex flex-col min-h-[430px]"
                  >
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <span className="rounded-full bg-fut-primary text-fut-bg-dark px-3 py-1.5 text-xs font-black uppercase tracking-[.14em]">
                        {pkg.tag}
                      </span>
                    </div>

                    <h3 className="text-2xl font-display font-black text-white mb-3">
                      {pkg.title}
                    </h3>

                    <p className="text-fut-primary font-bold mb-3 leading-relaxed">
                      {pkg.line}
                    </p>

                    <p className="text-fut-text-light/75 text-sm leading-relaxed mb-5">
                      {pkg.fit}
                    </p>

                    <div className="space-y-2 mb-5">
                      {pkg.includes.slice(0, 5).map((item) => (
                        <div key={item} className="flex gap-2">
                          <CheckIcon />
                          <p className="text-fut-text-light/90 text-sm">
                            {item}
                          </p>
                        </div>
                      ))}
                    </div>

                    {pkg.note && (
                      <p className="rounded-2xl bg-black/18 p-3 text-xs text-fut-text-light/75 mb-5">
                        {pkg.note}
                      </p>
                    )}

                    <a
                      href="/contact"
                      className="mt-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-primary text-fut-bg-dark px-5 py-3 font-black"
                    >
                      {pkg.cta}
                      <ArrowIcon />
                    </a>
                  </article>
                ))}

                <article className="xl:col-span-1 lg:col-span-2 rounded-[2.35rem] bg-fut-primary text-fut-bg-dark p-6 flex flex-col min-h-[430px] shadow-2xl shadow-fut-primary/12">
                  <span className="rounded-full bg-fut-bg-dark text-white px-3 py-1.5 text-xs font-black uppercase tracking-[.14em] w-fit mb-4">
                    {featured.tag}
                  </span>

                  <h3 className="text-3xl font-display font-black mb-3">
                    {featured.title}
                  </h3>

                  <p className="font-black mb-3 leading-relaxed">
                    {featured.line}
                  </p>

                  <p className="font-semibold opacity-90 text-sm leading-relaxed mb-5">
                    {featured.fit}
                  </p>

                  <div className="grid md:grid-cols-2 xl:grid-cols-1 gap-2 mb-5">
                    {featured.includes.slice(0, 6).map((item) => (
                      <div
                        key={item}
                        className="flex gap-2 rounded-2xl bg-fut-bg-dark/10 p-2"
                      >
                        <span className="font-black">✓</span>
                        <p className="text-sm font-semibold">{item}</p>
                      </div>
                    ))}
                  </div>

                  <a
                    href="/contact"
                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-bg-dark text-white px-5 py-3 font-black"
                  >
                    {featured.cta}
                    <ArrowIcon />
                  </a>
                </article>
              </div>

              <section className="mt-14">
                <div className="mb-8 max-w-5xl">
                  <p className="text-fut-primary font-black uppercase tracking-[.18em] text-sm mb-3">
                    {isSpanish ? "Planes premium" : "Premium plans"}
                  </p>
                  <h2 className="text-3xl md:text-5xl font-display font-black leading-tight text-white mb-4">
                    {isSpanish
                      ? "Acompañamiento para negocios que necesitan continuidad."
                      : "Guidance for businesses that need continuity."}
                  </h2>
                  <p className="text-fut-text-light/75 text-lg leading-relaxed">
                    {isSpanish
                      ? "Estos planes están pensados para trabajar contigo durante el año, no solo en una tarea aislada. Buscan sostener estructura, orden financiero, cumplimiento y seguimiento estratégico."
                      : "These plans are designed to work with you throughout the year, not just on one isolated task. They support structure, financial order, compliance, and strategic follow-up."}
                  </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {premiumPlans.map((plan, index) => (
                    <article
                      key={plan.title}
                      className="relative overflow-hidden rounded-[3rem] bg-gradient-to-br from-fut-primary/18 via-white/[.05] to-white/[.025] p-6 md:p-8 shadow-2xl shadow-fut-primary/10 hover:shadow-fut-primary/25 transition-all duration-300"
                    >
                      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-fut-primary/20 blur-3xl" />
                      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-blue-500/10 blur-3xl" />

                      <div className="relative z-10">
                        <div className="flex flex-wrap items-center gap-3 mb-5">
                          <span className="rounded-full bg-fut-primary text-fut-bg-dark px-4 py-2 text-xs font-black uppercase tracking-[.16em]">
                            {plan.tag}
                          </span>
                          <span className="text-fut-primary/80 font-black text-sm">
                            0{index + 1}
                          </span>
                        </div>

                        <h3 className="text-3xl md:text-4xl font-display font-black text-white mb-3">
                          {plan.title}
                        </h3>

                        <p className="text-fut-primary font-black leading-relaxed mb-4">
                          {plan.line}
                        </p>

                        <p className="text-fut-text-light/78 leading-relaxed mb-6">
                          {plan.objective}
                        </p>

                        <div className="grid sm:grid-cols-2 gap-3 mb-6">
                          {plan.includes.map((item) => (
                            <div key={item} className="flex gap-2 rounded-2xl bg-black/18 p-3">
                              <CheckIcon />
                              <p className="text-fut-text-light/86 text-sm leading-relaxed">
                                {item}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="rounded-[2rem] bg-fut-primary/12 p-5 mb-6">
                          <p className="text-white font-semibold leading-relaxed">
                            {plan.result}
                          </p>
                        </div>

                        <a
                          href="/contact"
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-primary text-fut-bg-dark px-6 py-4 font-black shadow-xl shadow-fut-primary/12"
                        >
                          {plan.cta}
                          <ArrowIcon />
                        </a>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <div className="mt-8 rounded-[2.35rem] bg-black/22 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
                <div>
                  <h3 className="text-2xl md:text-3xl font-display font-black text-fut-primary">
                    {t.finalTitle}
                  </h3>

                  <p className="text-fut-text-light/75 mt-2 max-w-3xl">
                    {t.finalText}
                  </p>
                </div>

                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-primary text-fut-bg-dark px-6 py-4 font-black shrink-0"
                >
                  {t.finalCta}
                  <ArrowIcon />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 z-50 grid grid-cols-2 md:hidden border-t border-fut-primary/30">
        <a
          href="tel:+18884536026"
          className="bg-white text-fut-bg-dark py-4 text-center font-black"
        >
          {t.callLabel}
        </a>

        <a
          href="https://wa.me/19495687938"
          className="bg-fut-primary text-fut-bg-dark py-4 text-center font-black"
        >
          {t.whatsappLabel}
        </a>
      </div>
    </main>
  );
};

export default PackagesPricingPage;
